import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Shield, 
  Plus, 
  Trash2, 
  Globe, 
  AlertTriangle, 
  CheckCircle,
  Copy,
  ExternalLink,
  Monitor
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const IPManagementPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ipRestrictionEnabled, setIpRestrictionEnabled] = useState(false);
  const [allowedIPs, setAllowedIPs] = useState([]);
  const [myIP, setMyIP] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ ip_address: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const bypassURL = `${window.location.origin}/admin/nikhil/9696`;

  useEffect(() => {
    if (isAdmin()) {
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, ipsRes, myIpRes] = await Promise.all([
        axios.get(`${API_URL}/ip/settings`),
        axios.get(`${API_URL}/ip/allowed`),
        axios.get(`${API_URL}/ip/my-ip`)
      ]);
      setIpRestrictionEnabled(settingsRes.data.enabled || false);
      setAllowedIPs(ipsRes.data);
      setMyIP(myIpRes.data.ip_address);
    } catch (error) {
      console.error('Failed to fetch IP data:', error);
      toast.error('Failed to load IP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRestriction = async () => {
    setToggling(true);
    try {
      const response = await axios.post(`${API_URL}/ip/settings/toggle`);
      setIpRestrictionEnabled(response.data.enabled);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Failed to toggle IP restriction:', error);
      toast.error('Failed to update IP restriction setting');
    } finally {
      setToggling(false);
    }
  };

  const handleAddIP = async (e) => {
    e.preventDefault();
    if (!formData.ip_address) {
      toast.error('Please enter an IP address');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/ip/allowed`, formData);
      toast.success('IP address added to whitelist');
      setDialogOpen(false);
      setFormData({ ip_address: '', description: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to add IP:', error);
      toast.error(error.response?.data?.detail || 'Failed to add IP address');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMyIP = async () => {
    try {
      const response = await axios.post(`${API_URL}/ip/add-my-ip`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Failed to add my IP:', error);
      toast.error('Failed to add your IP');
    }
  };

  const handleRemoveIP = async (ipId) => {
    if (!window.confirm('Are you sure you want to remove this IP from whitelist?')) return;

    try {
      await axios.delete(`${API_URL}/ip/allowed/${ipId}`);
      toast.success('IP removed from whitelist');
      fetchData();
    } catch (error) {
      console.error('Failed to remove IP:', error);
      toast.error('Failed to remove IP');
    }
  };

  const copyBypassURL = () => {
    navigator.clipboard.writeText(bypassURL);
    toast.success('Bypass URL copied to clipboard');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
        <p className="text-slate-500">Only administrators can manage IP restrictions.</p>
      </div>
    );
  }

  return (
    <div data-testid="ip-management-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">
          IP Restriction Management
        </h1>
        <p className="text-slate-500 mt-1">Control access to the portal by IP address</p>
      </div>

      {loading ? (
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* IP Restriction Toggle */}
            <Card className={`border-2 ${ipRestrictionEnabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ipRestrictionEnabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <Shield className={`w-5 h-5 ${ipRestrictionEnabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">IP Restriction</p>
                      <p className={`text-sm ${ipRestrictionEnabled ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {ipRestrictionEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={ipRestrictionEnabled}
                    onCheckedChange={handleToggleRestriction}
                    disabled={toggling}
                    data-testid="ip-restriction-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Your IP */}
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Monitor className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">Your Current IP</p>
                    <p className="text-sm text-slate-600 font-mono">{myIP}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={handleAddMyIP} data-testid="add-my-ip-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Whitelisted Count */}
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Whitelisted IPs</p>
                    <p className="text-sm text-slate-600">{allowedIPs.length} IP addresses</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Warning when enabled */}
          {ipRestrictionEnabled && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900">IP Restriction is Active</p>
                    <p className="text-sm text-amber-700">
                      Only whitelisted IP addresses can access this portal. Make sure your IP is added before logging out.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Admin Bypass URL */}
          <Card className="border-indigo-200 bg-indigo-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-indigo-600" />
                Admin Bypass URL
              </CardTitle>
              <CardDescription>
                Use this link to bypass IP restrictions (saves a cookie for 30 days)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Input
                  value={bypassURL}
                  readOnly
                  className="font-mono text-sm bg-white"
                  data-testid="bypass-url-input"
                />
                <Button variant="outline" onClick={copyBypassURL} data-testid="copy-bypass-url-btn">
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" asChild>
                  <a href="/admin/nikhil/9696" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Allowed IPs Table */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Whitelisted IP Addresses</CardTitle>
                <CardDescription>IPs that can access the portal when restriction is enabled</CardDescription>
              </div>
              <Button onClick={() => setDialogOpen(true)} data-testid="add-ip-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add IP
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {allowedIPs.length === 0 ? (
                <div className="text-center py-12">
                  <Globe className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900">No IPs whitelisted</h3>
                  <p className="text-slate-500 mb-4">Add IP addresses to allow access when restriction is enabled</p>
                  <Button onClick={() => setDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add IP Address
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">IP Address</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="font-semibold">Added By</TableHead>
                      <TableHead className="font-semibold">Added At</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allowedIPs.map((ip) => (
                      <TableRow key={ip.id} data-testid={`ip-row-${ip.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium text-slate-900">{ip.ip_address}</span>
                            {ip.ip_address === myIP && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                Your IP
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {ip.description || '-'}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {ip.added_by_name}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {formatDate(ip.added_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveIP(ip.id)}
                            data-testid={`remove-ip-${ip.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add IP Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add IP Address</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddIP} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ip_address">IP Address</Label>
              <Input
                id="ip_address"
                value={formData.ip_address}
                onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                placeholder="e.g., 192.168.1.1"
                required
                data-testid="ip-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Office Network"
                data-testid="ip-description-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-ip-btn">
                {submitting ? 'Adding...' : 'Add IP'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IPManagementPage;
